/**
 * ThunderbirdImportPage – Bulk-Import von Thunderbird MBOX-Ordnern
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Mail, FolderOpen, Play, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, Users, Paperclip, Inbox, Search, Clock
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

async function fetchJson(path, options = {}) {
  const token = localStorage.getItem('olympus_token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}

function Bar({ percent, color = 'bg-blue-500' }) {
  const p = Math.max(0, Math.min(100, percent || 0));
  return (
    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${p}%` }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className={`text-2xl font-bold ${accent || 'text-slate-800'}`}>{value ?? '-'}</div>
    </div>
  );
}

export default function ThunderbirdImportPage() {
  const [folderPath, setFolderPath] = useState(
    'C:\\Users\\ProCura\\HiDrive\\Geschäftsdaten\\ProCura Bayern\\Lokaler Emailordner'
  );
  const [job, setJob] = useState(null);
  const [stats, setStats] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  // Email-Suche
  const [emails, setEmails] = useState([]);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsTotal, setEmailsTotal] = useState(0);

  const loadJob = useCallback(async () => {
    try {
      const j = await fetchJson('/api/thunderbird/job-status');
      setJob(j.status === 'kein_job' ? null : j);
      const s = await fetchJson('/api/thunderbird/stats');
      setStats(s);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const searchEmails = useCallback(async () => {
    setEmailsLoading(true);
    try {
      const qs = emailSearch ? `?search=${encodeURIComponent(emailSearch)}&limit=30` : '?limit=30';
      const r = await fetchJson('/api/thunderbird/emails' + qs);
      setEmails(r.emails || []);
      setEmailsTotal(r.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setEmailsLoading(false);
    }
  }, [emailSearch]);

  useEffect(() => {
    loadJob();
    const id = setInterval(loadJob, 5000);
    return () => clearInterval(id);
  }, [loadJob]);

  useEffect(() => {
    searchEmails();
  }, [searchEmails]);

  const startImport = async () => {
    setStarting(true);
    setError(null);
    try {
      const r = await fetchJson('/api/thunderbird/scan-start', {
        method: 'POST',
        body: JSON.stringify({ folder_path: folderPath }),
      });
      if (r.status === 'bereits_aktiv') {
        setError('Ein Import-Job läuft bereits. Bitte warten oder abbrechen.');
      }
      await loadJob();
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const isRunning = job && ['wartend', 'scannt', 'importiert'].includes(job.status);
  const emailsProgress = job?.emails_gesamt
    ? Math.round((job.emails_verarbeitet / job.emails_gesamt) * 100)
    : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="thunderbird-import-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
          <Mail className="w-6 h-6" />
          Thunderbird Bulk-Import
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Importiert lokale Thunderbird-Ordner (MBOX) in Olympus. Anhänge bleiben auf Windows-PC.
        </p>
      </div>

      {/* Import-Start */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h2 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <FolderOpen className="w-5 h-5" /> Import starten
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Thunderbird-Ordner auf Windows-PC
            </label>
            <input
              type="text"
              value={folderPath}
              onChange={e => setFolderPath(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md font-mono text-sm"
              placeholder="C:\Users\..."
              data-testid="tb-folder-input"
              disabled={isRunning}
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={startImport}
              disabled={isRunning || starting || !folderPath.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-md font-medium"
              data-testid="tb-start-btn"
            >
              {starting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {starting ? 'Startet...' : isRunning ? 'Import läuft...' : 'Import starten'}
            </button>
            <button
              onClick={loadJob}
              className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md hover:bg-slate-50 text-sm"
              data-testid="tb-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" /> Aktualisieren
            </button>
          </div>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Laufender Job */}
      {job && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="tb-job-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="w-5 h-5" /> Aktueller Job
            </h2>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
              job.status === 'fertig' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              job.status === 'fehler' ? 'bg-red-50 text-red-700 border-red-200' :
              'bg-blue-50 text-blue-700 border-blue-200'
            }`}>
              {job.status}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard icon={Inbox} label="Emails verarbeitet" value={`${job.emails_verarbeitet || 0} / ${job.emails_gesamt || '?'}`} />
            <StatCard icon={Users} label="Zugeordnet" value={job.emails_zugeordnet || 0} accent="text-emerald-600" />
            <StatCard icon={AlertTriangle} label="Unzugeordnet" value={job.emails_unzugeordnet || 0} accent="text-amber-600" />
            <StatCard icon={Paperclip} label="Neue Anhänge" value={`${job.anhaenge_neu || 0} (von ${job.anhaenge_gesamt || 0})`} />
          </div>

          {job.emails_gesamt > 0 && (
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1 text-slate-500">
                <span>Fortschritt</span>
                <span>{emailsProgress}%</span>
              </div>
              <Bar percent={emailsProgress} color="bg-blue-500" />
            </div>
          )}

          {job.aktuelle_mbox && (
            <div className="text-xs text-slate-500">
              <span className="font-medium">Aktuell:</span> {job.aktuelle_mbox}
            </div>
          )}

          {job.letzter_fehler && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 font-mono">
              {job.letzter_fehler}
            </div>
          )}
        </div>
      )}

      {/* Gesamt-Statistik */}
      {stats && stats.emails_gesamt > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-semibold text-slate-700 mb-4">Gesamt-Bestand</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Inbox} label="Emails gesamt" value={stats.emails_gesamt} />
            <StatCard icon={CheckCircle2} label="Zugeordnet" value={stats.emails_zugeordnet} accent="text-emerald-600" />
            <StatCard icon={XCircle} label="Unzugeordnet" value={stats.emails_unzugeordnet} accent="text-amber-600" />
            <StatCard icon={Paperclip} label="Anhänge (neu/gesamt)" value={`${stats.anhaenge_neu} / ${stats.anhaenge_gesamt}`} />
          </div>
        </div>
      )}

      {/* Email-Suche */}
      {stats && stats.emails_gesamt > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
            <Search className="w-5 h-5 text-slate-600" />
            <input
              type="text"
              placeholder="Email durchsuchen (Betreff, Body, Absender)..."
              value={emailSearch}
              onChange={e => setEmailSearch(e.target.value)}
              className="flex-1 px-3 py-1.5 border border-slate-200 rounded-md text-sm"
              data-testid="tb-email-search"
            />
            <span className="text-xs text-slate-500">{emailsTotal} Treffer</span>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {emailsLoading ? (
              <div className="p-5 text-center text-slate-500">
                <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Lade...
              </div>
            ) : emails.length === 0 ? (
              <div className="p-5 text-center text-slate-500 text-sm">Keine Treffer.</div>
            ) : (
              emails.map(e => (
                <div key={e.email_id} className="px-5 py-3 hover:bg-slate-50">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="font-medium text-sm text-slate-800 line-clamp-1">{e.subject || '(ohne Betreff)'}</div>
                    <div className="text-xs text-slate-400 flex-shrink-0">
                      {e.date ? new Date(e.date).toLocaleDateString('de-DE') : ''}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-3">
                    <span className="truncate">{e.from}</span>
                    {e.klient_id ? (
                      <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] flex-shrink-0">zugeordnet</span>
                    ) : (
                      <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] flex-shrink-0">offen</span>
                    )}
                    {e.anhang_count > 0 && (
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <Paperclip className="w-3 h-3" /> {e.anhang_count}
                      </span>
                    )}
                  </div>
                  {e.folder_path && (
                    <div className="text-xs text-slate-400 mt-1 font-mono">{e.folder_path}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
